<template>
  <v-container class="availability-grid">
    <v-row>
      <v-col class="availability-grid-legend-col" cols="6" md="4">
        <div class="availability-grid-legend"
             style="background-color: rgba(0, 0, 0, 0.25);"><label></label>
        </div>
        <div class="availability-grid-legend-text"><label>{{ $t('booking.unavailable') }}</label></div>
      </v-col>
      <v-col class="availability-grid-legend-col" cols="6" md="4">
        <div class="availability-grid-legend" style="background-color: rgba(111, 193, 182, 0.5)"><label></label></div>
        <div class="availability-grid-legend-text"><label>{{ $t('booking.available') }}</label></div>
      </v-col>
      <v-col cols="12" md="4">
        <div v-for="tier in priceTierMap" v-bind:key="tier.id" class="text-right">
          <span style="font-weight: bold">{{ tier.legend }}</span> - {{ tier.name }} - ${{ tier.price }}
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-calendar type="week"
                    :start="fromDate"
                    :end="toDate"
                    :weekdays="[1,2,3,4,5,6,0]"
                    :events="sessions"
                    :event-color="getSessionColor"
                    :first-interval="firstInterval"
                    :interval-count="intervalCount"
                    :interval-height="32"
                    :interval-width="45"
                    @click:event="sessionClicked"
                    :locale="$i18n.locale"
                    :interval-format="getIntervalFormat"
                    :class="{'ie-fix': isIE}">
          <template v-slot:event="{event}">
            <label :class="['availability-cell-label', {'selected': selectedSessionIds.indexOf(event.id) !== -1}]">
              <!--              {{ priceTierMap[event.price_tier].name.substr(0, 1).toUpperCase() }}-->
              {{ priceTierMap[event.price_tier].legend }}
            </label>
          </template>
        </v-calendar>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import _ from 'lodash'
import {DateTime} from 'luxon'
import {ref, computed, watch, onMounted} from "@vue/composition-api";

export default {
  name: 'AvailabilityGrid',
  props: {
    typeId: String,
    fromDate: String,
    toDate: String,
    maxDailySession: Number,
  },
  setup(props, {root, emit}) {
    const isIE = computed(() => {
      let userAgent = window.navigator.userAgent;
      let msie = userAgent.indexOf("MSIE ");

      if (msie > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./) || /Edge/.test(navigator.userAgent))  // If Internet Explorer, return version number
      {
        return true;
      } else {
        return false;
      }
    });

    const sessions = ref([]);
    const selectedSessions = ref([]);
    const selectedSessionIds = computed(() => {
      return selectedSessions.value.map((s) => s.id);
    });
    const priceTierMap = ref({});
    const existingBookings = ref([]);
    const getSessions = function () {
      root.$callAPI('room/search/type_schedules/', {
        item_type_id: props.typeId,
        from_date: DateTime.fromISO(props.fromDate).toSeconds(),
        to_date: DateTime.fromISO(props.toDate).toSeconds() + 86400,
      }, function (response) {
        sessions.value = response.body.sessions.map((s) => {
          let start = DateTime.fromISO(s.start);
          let end = DateTime.fromISO(s.end);

          if (!start.hasSame(end, 'day')) {
            end = start.set({hour: 23, minute: 59});
          }

          return {
            ...s,
            start: start.toFormat('yyyy-MM-dd HH:mm'),
            end: end.toFormat('yyyy-MM-dd HH:mm'),
          }
        });

        let map = {};
        response.body.price_tiers.forEach((p) => {
          map[p.id] = p;
        });
        priceTierMap.value = map;
        existingBookings.value = response.body.existing_bookings;
      })
    };

    watch([() => props.fromDate, () => props.toDate], () => {
      getSessions();
    })

    const getIntervalFormat = function (time) {
      if (time.hour === 12) return root.$t('interval_noon');

      let isPM = time.hour > 12;

      return root.$t('interval_format', {
        hour: time.hour % 12,
        am_pm: isPM ? root.$t('interval_pm') : root.$t('interval_am'),
      });
    }
    const sessionHasOverlap = function (session) {
      let session_start = DateTime.fromFormat(session.start, 'yyyy-MM-dd HH:mm')
      let session_end = DateTime.fromFormat(session.end, 'yyyy-MM-dd HH:mm')
      return existingBookings.value.some((b) => {
        let start = DateTime.fromISO(b.start_time);
        let end = DateTime.fromISO(b.end_time);
        if (start < session_end && end > session_start) {
          return true
        }
      });
    }
    const getSessionColor = function (session) {
      if (DateTime.fromFormat(session.start, 'yyyy-MM-dd HH:mm') <= DateTime.fromJSDate(new Date)) {
        return '#c6c6c6';
      }

      if (selectedSessionIds.value.indexOf(session.id) !== -1) {
        return '#1a8ffb';
      }

      // Check Overlapping Booking
      if (sessionHasOverlap(session)) {
        return '#c6c6c6';
      }

      return {
        0: '#b9e0da',
        1: '#c6c6c6',
        2: '#c6c6c6',
      }[session.status];
    };
    const sessionClicked = function ({event}) {
      if (DateTime.fromFormat(event.start, 'yyyy-MM-dd HH:mm') <= DateTime.fromJSDate(new Date) || event.status !== 0 || sessionHasOverlap(event)) {
        return;
      }

      let idx = -1;

      selectedSessions.value.forEach((s, index) => {
        if (s.id === event.id) {
          idx = index;
        }
      });

      let newSelectedSessions = selectedSessions.value;
      if (idx === -1) {
        // Check if selected session connects with existing sessions
        let connected = newSelectedSessions.length === 0;

        newSelectedSessions.forEach((s) => {
          if (s.start === event.end || s.end === event.start) {
            connected = true;
          }
        });

        if (!connected) {
          newSelectedSessions = [];
        }

        // Check daily limit
        if (props.maxDailySession !== -1) {
          let day_start = DateTime.fromFormat(event.start, 'yyyy-MM-dd HH:mm').startOf('day');
          let day_end = DateTime.fromFormat(event.start, 'yyyy-MM-dd HH:mm').endOf('day');

          let existing = existingBookings.value.filter((b)=> {
            let start = DateTime.fromISO(b.start_time);
            let end = DateTime.fromISO(b.end_time);
            return start < day_end && end > day_start
          })

          if (newSelectedSessions.length + existing.length + 1 > props.maxDailySession) {
            root.$showAlertMessage(root.$t('error_messages.booking_limit_reached'));
            return;
          }
        }

        newSelectedSessions.push(event);
        selectedSessions.value = _.sortBy(newSelectedSessions, [(s) => {
          return s.start;
        }]);
      } else {
        selectedSessions.value = [];
      }
    };

    watch(selectedSessions, (newVal) => {
      emit('input', newVal);
    });


    const firstInterval = computed(() => {
      let minStart = _.min(sessions.value.map((s) => {
        let start = DateTime.fromFormat(s.start, 'yyyy-MM-dd HH:mm');
        return (start.toSeconds() + start.offset * 60) % (24 * 3600);
      })) || 0;

      // return Math.floor(minStart / 3600);
      return Math.floor(minStart / 3600) - 1;
    });
    const intervalCount = computed(() => {
      let a = sessions.value.map((s) => {
        let start = DateTime.fromFormat(s.start, 'yyyy-MM-dd HH:mm');
        let end = DateTime.fromFormat(s.end, 'yyyy-MM-dd HH:mm');

        if (start.hasSame(end, 'day')) {
          return Math.ceil((end.toSeconds() + end.offset * 60) % (24 * 3600) / 3600);
        } else {
          return 24;
        }
      });

      // return (_.max(a) || 24) - firstInterval.value;
      return (_.max(a) || 24) - firstInterval.value + 1;
    });

    onMounted(() => {
      getSessions();
    })

    return {
      isIE,
      sessions,
      selectedSessionIds,
      priceTierMap,
      getIntervalFormat,
      getSessionColor,
      sessionClicked,
      firstInterval,
      intervalCount,
    }
  },
}
</script>

<style lang="less">
.ie-fix {
  .v-calendar-daily__body {
    flex-basis: auto !important;
  }
}

.availability-grid {
  text-align: center;
  padding: 0;
  text-transform: uppercase;

  .availability-grid-legend-col {
    text-align: center;

    @media only screen and (max-width: 600px) {
      text-align: center;
    }
  }

  .availability-grid-legend {
    display: inline-block;
    width: 45px;
    height: 45px;
    /*max-width: 11vw;*/
    max-height: 11vw;
    /*line-height: 11vw;*/
    color: white;

    position: relative;
    padding: 0;

    label {
      margin: 0;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      @media only screen and (max-width: 600px) {
        font-size: 0.9rem;
      }
    }
  }

  .availability-grid-legend-text {
    display: inline-block;
    height: 45px;
    max-height: 11vw;
    /*line-height: 11vw;*/

    position: relative;
    padding: 0;

    label {
      margin: 0;
      padding-left: 8px;
      //position: absolute;
      top: 50%;
      left: 0%;
      text-overflow: unset;
      white-space: nowrap;
      transform: translate(0%, -50%);

      @media only screen and (max-width: 600px) {
        font-size: 0.9rem;
      }
    }
  }

  .v-calendar {
    border-top: none !important;
    border-left: none !important;

    .v-calendar-daily__day-interval {
      border-top: none !important;

      &:last-child {
        display: none;
      }
    }


    .v-calendar-daily__scroll-area {
      margin-top: -32px;
    }

    .v-calendar-daily__interval:first-child {
      &:after {
        border-top: none !important;
      }
    }

    .v-calendar-daily__interval:last-child {
      &:after {
        border-bottom: none !important;
      }
    }

    .v-calendar-daily__interval-text {
      top: -3px !important;
      font-size: 8px !important;
    }

    .v-calendar-daily_head-day:last-child {
      border-right: none !important;
    }

    .v-calendar-daily__intervals-body {
      border-right: none !important;
    }

    .v-calendar-daily__day {
      border-right: none !important;
      border-bottom: none !important;

      &:last-child {
        border-right: none !important;
      }
    }

    .v-event-timed-container {
      margin-right: 0 !important;
    }

    .v-event-timed {
      border-radius: 0;
    }

    .availability-cell-label {
      font-size: 16px;
      user-select: none;
      margin: 0;
      position: absolute;
      top: 50%;
      left: 50%;
      -webkit-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);

      &.selected {
        font-weight: bold;
      }
    }
  }

  .v-btn--fab.v-size--default {
    height: initial;
    width: initial;
  }
}
</style>
